/**
 * Y-axis bounds for bar charts that carry a target reference line.
 *
 * Recharts' default `domain={[0, 'auto']}` fits the axis to the DATA, so a
 * reference line above every bar falls outside the domain and renders
 * off-chart — the Monthly Revenue report's target line was invisible at the
 * 10,000,000/month default because no month reached it. Feeding the axis an
 * explicit max derived from `max(dataMax, target)` keeps the line on screen
 * whatever the target is set to.
 */

/**
 * Mantissas a "nice" axis maximum may take, as multiples of a power of ten.
 *
 * Every entry divides by 4 into a value with at most two decimals, so the
 * four-interval tick run in `axisTicks` never produces labels like
 * `11.3M` — which is what a naive `dataMax * 1.1` domain gives.
 */
const NICE_MANTISSAS = [1, 1.2, 1.6, 2, 2.4, 3.2, 4, 4.8, 6, 8, 10] as const;

/** How many intervals the axis is divided into (so `count + 1` tick labels). */
export const AXIS_INTERVALS = 4;

/**
 * How far above the tallest bar the axis may stretch to accommodate a
 * reference line before it stops and lets the line clip instead.
 *
 * Without a cap, a reference line far above the data flattens the bars into
 * the bottom of the plot: the Monthly Revenue report's catch-up target reaches
 * a whole year's shortfall in December (30.8M against a ~6M monthly run rate
 * in 2025), which left the bars occupying a sixth of the chart. Two means the
 * bars always fill at least half the height, and the target line simply runs
 * off the top in the months where it is genuinely out of reach — its exact
 * figure is still on the tile and in the tooltip.
 */
export const AXIS_TARGET_CAP_MULTIPLE = 2;

/**
 * Smallest "nice" number STRICTLY greater than `value`.
 *
 * Strictly greater, not "greater or equal", so a target that happens to be a
 * round number does not land exactly on the top of the plot area — where the
 * dashed line would be indistinguishable from the chart's own top border and
 * its label would collide with the topmost tick.
 *
 * Returns 0 for a non-positive or non-finite input, which the caller treats as
 * "no explicit domain — let Recharts decide" (e.g. an all-zero series).
 */
export function niceAxisMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;

  const exponent = Math.floor(Math.log10(value));
  const magnitude = 10 ** exponent;
  const mantissa = value / magnitude;

  // A tiny epsilon guards float noise (e.g. 6e6 / 1e6 === 5.999999999999999),
  // which would otherwise pick the SAME mantissa and return `value` itself.
  const nice = NICE_MANTISSAS.find((m) => m > mantissa + 1e-9);
  return nice !== undefined ? nice * magnitude : 10 * magnitude;
}

/**
 * Evenly spaced tick values from 0 to `max` inclusive, `AXIS_INTERVALS` apart.
 * Empty when `max` is not a usable maximum, matching `niceAxisMax`'s 0 return.
 */
export function axisTicks(max: number, intervals = AXIS_INTERVALS): number[] {
  if (!Number.isFinite(max) || max <= 0 || intervals < 1) return [];
  return Array.from({ length: intervals + 1 }, (_, i) => (max / intervals) * i);
}

/**
 * Axis maximum for a chart whose bars carry a reference line: high enough to
 * show the line, but never more than `capMultiple` times the tallest bar.
 *
 * When the line fits under the cap the axis simply clears it. When it does
 * not, the axis stops at the cap and the caller lets the line clip — bar
 * legibility wins over showing an out-of-reach target in full, since the
 * figure itself is reported elsewhere.
 *
 * With no bars at all (`dataMax` 0) there is nothing to protect, so the axis
 * clears the line however high it is. Returns 0 when neither input can bound
 * an axis, which the caller reads as "let Recharts decide".
 */
export function cappedAxisMax(
  dataMax: number,
  referenceMax: number,
  capMultiple = AXIS_TARGET_CAP_MULTIPLE,
): number {
  const data = Number.isFinite(dataMax) && dataMax > 0 ? dataMax : 0;
  const reference = Number.isFinite(referenceMax) && referenceMax > 0 ? referenceMax : 0;

  if (data <= 0) return niceAxisMax(reference);
  return niceAxisMax(Math.min(Math.max(data, reference), data * capMultiple));
}
